import BigNumber from 'bignumber.js';

/**
 * Calculates the position size based on the input USD amount, execution fee, leverage, and open position fee.
 *
 * The formula used is:
 * x = l(u - f(x))
 *
 * x = position size
 * l = leverage
 * u = fromUsd
 * f(x) = executionFeeUsd + feeConfigBasisPoints * x
 *
 *
 * Final: x = l(u - f) / (1 + l(p / 10000))
 *
 * nextToUsd = leverage * (fromUsd - executionFeeUsd - feeConfigBasisPoints * nextToUsd)
 *
 * Simplified to:
 * fromUsdAfterExeFee = fromUsd - executionFeeUsd
 * feeConfigBasisPoints = openFeeP
 *
 * nextToUsd = leverage * fromUsdAfterExeFee - leverage * feeConfigBasisPoints * nextToUsd
 * nextToUsd = leverage * fromUsdAfterExeFee / (1 + leverage * feeConfigBasisPoints)
 * nextToUsd = leverage * fromUsdAfterExeFee / (1 + leverage * openFeeP)
 *
 * @param fromUsd - The input USD amount.
 * @param executionFeeUsd - The execution fee in USD.
 * @param leverage - The leverage value.
 * @param openFeeP - The open position fee in basis points.
 * @param isBeforeFee - return positionSize before open positionFees
 * @returns The calculated position size.
 */
type GetPositionSizeParams = {
  fromUsd: BigNumber;
  executionFeeUsd: BigNumber;
  leverage: number;
  openFeeP: number;
  isBeforeFee?: boolean;
};
export const getPositionSize = ({
  fromUsd,
  executionFeeUsd,
  leverage,
  openFeeP,
  isBeforeFee = false,
}: GetPositionSizeParams): BigNumber => {
  if (isBeforeFee) return fromUsd.multipliedBy(leverage);

  // Calculate the USD amount after subtracting the execution fee
  const fromUsdAfterExeFee = fromUsd.minus(executionFeeUsd);

  // Calculate the numerator: leverage * fromUsdAfterExeFee
  const numerator = fromUsdAfterExeFee.multipliedBy(leverage);

  // Calculate the denominator: 1 + leverage * (openFeeP)
  const denominator = 1 + leverage * openFeeP;

  // Calculate the position size: numerator / denominator
  return numerator.dividedBy(denominator);
};
